import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwUpdate } from '@angular/service-worker';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, map, of, filter } from 'rxjs';
import { url_servicios } from 'src/app/config/config';
import { environment } from 'src/environments/environment';
import { StorageService } from '../../shared/storage/storage.service';
import { DashboardRoutes } from '../../shared/routes/dashboard.routes';

import Swal from 'sweetalert2';

interface VersionResponse {
  version: string;
}

const VERSION_MISMATCH_DISMISSED = 'version_mismatch_dismissed';

@Injectable({
  providedIn: 'root',
})
export class VersionService {
  private readonly http = inject(HttpClient);
  private readonly storageService = inject(StorageService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);

  private versionMismatch = new BehaviorSubject<boolean>(false);
  private showAlert = new BehaviorSubject<boolean>(false);
  private versionChecked = false;

  constructor() {
    // Subscribe to version mismatch changes to update the alert state
    this.versionMismatch.subscribe(mismatch => {
      if (!mismatch) {
        this.showAlert.next(false);
        return;
      }

      // Check if the user has dismissed the alert
      const dismissed = this.storageService.get<boolean>(VERSION_MISMATCH_DISMISSED);
      this.showAlert.next(mismatch && !dismissed);
    });
  }

  public initUpdateListener(): void {
    if (isPlatformBrowser(this.platformId) && this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.pipe(
        filter(evt => evt.type === 'VERSION_READY')
      ).subscribe(() => {
        const isHome = this.router.url === DashboardRoutes.home;
        if (isHome) {
          this.swUpdate.activateUpdate().then(() => this.reloadPage());
        } else {
          Swal.fire({
            title: "There is a new app version",
            text: "Would you like to update?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes"
          }).then((result) => {
            if (result.isConfirmed) {
              this.swUpdate.activateUpdate().then(() => this.reloadPage());
            }
          });
        }
      });
    }
  }

  public reloadPage(): void {
    window.location.reload();
  }

  /**
   * Checks if the frontend version matches the backend version
   * Only performs the check once per app session
   */
  checkVersion(): Observable<boolean> {
    // If we've already checked the version, return the current state
    if (this.versionChecked) {
      return this.versionMismatch.asObservable();
    }

    // Mark that we've checked the version
    this.versionChecked = true;

    // Get the frontend version from environment
    const frontendVersion = environment.version;

    // Make a request to the backend to get its version
    return this.http.get<VersionResponse>(`${url_servicios}/version`).pipe(
      map((response) => {
        const backendVersion = response.version;
        const mismatch = frontendVersion !== backendVersion;

        // Update the mismatch state
        this.versionMismatch.next(mismatch);
        return mismatch;
      }),
      catchError(() => {
        // If there's an error, assume versions match to avoid disrupting the user
        this.versionMismatch.next(false);
        return of(false);
      })
    );
  }

  /**
   * Returns an observable that emits true if there's a version mismatch
   * and the user hasn't dismissed the alert
   */
  shouldShowMismatchAlert(): Observable<boolean> {
    return this.showAlert.asObservable();
  }

  /**
   * Dismisses the version mismatch alert
   */
  dismissMismatchAlert(): void {
    this.storageService.set(VERSION_MISMATCH_DISMISSED, true);
    this.showAlert.next(false);
  }

  /**
   * Resets the dismissed state when the app is reloaded
   * This should be called when the app initializes
   */
  resetDismissedState(): void {
    this.storageService.remove(VERSION_MISMATCH_DISMISSED);
  }
}
